/**
 * LATM Public Design Tokens — locked single-source-of-truth for ALL public-facing
 * visual identity. No future phase may bypass this file.
 *
 * Mirrors the CSS custom properties defined in /app/frontend/src/index.css
 * (`:root --lifted-*`) so JS/TS consumers can read tokens without inlining.
 *
 * To change a token globally, update /app/frontend/src/index.css AND this file.
 */

export const TOKENS = Object.freeze({
  // ─── Color palette ──────────────────────────────────────────────────────────
  color: Object.freeze({
    cosmosBlack:  "#040506",     // base canvas
    surface:      "#0c0d10",
    surfaceAlt:   "#101216",
    border:       "rgba(255,255,255,0.08)",
    text:         "#f6f8fa",
    textDim:      "#b9c2cc",
    textMute:     "#8b95a1",
    accentGreen:  "#b6ff1a",     // LiftedGreen — primary neon
    accentGreenSoft: "rgba(182,255,26,0.18)",
    accentPurple: "#a78bfa",
    accentPurpleSoft: "rgba(167,139,250,0.18)",
    alertCritical: "#fb7185",
    alertWarning:  "#facc15",
    alertOk:       "#34d399",
  }),

  // ─── Typography ─────────────────────────────────────────────────────────────
  font: Object.freeze({
    display: "'Anton', sans-serif",        // headlines / wordmark
    label:   "'Bebas Neue', sans-serif",   // eyebrow / chip labels
    body:    "'Plus Jakarta Sans', system-ui, sans-serif",
  }),

  // ─── Type scale ─────────────────────────────────────────────────────────────
  type: Object.freeze({
    h1: { mobile: "text-4xl", tablet: "sm:text-5xl", desktop: "lg:text-6xl" },
    h2: { mobile: "text-3xl", tablet: "sm:text-4xl", desktop: "lg:text-5xl" },
    h3: { mobile: "text-2xl", tablet: "sm:text-3xl" },
    body:    "text-base",
    small:   "text-sm",
    eyebrow: "text-xs tracking-[0.18em] uppercase",
  }),

  // ─── Spacing rhythm ─────────────────────────────────────────────────────────
  space: Object.freeze({
    sectionY:        "py-12 sm:py-16",
    sectionYLg:      "py-16 sm:py-24",
    containerX:      "px-4 sm:px-6 lg:px-10",
    containerMaxW:   "max-w-7xl",
    contentMaxW:     "max-w-2xl",
  }),

  // ─── Radius / elevation ─────────────────────────────────────────────────────
  radius: Object.freeze({
    tile: "rounded-2xl",
    card: "rounded-xl",
    chip: "rounded-full",
  }),

  // ─── Asset tag taxonomy (Brand Asset Library single source of truth) ────────
  tags: Object.freeze({
    wordmark:  "wordmark.primary",
    xMascot:   "mascot.brand-ambassador",  // primary public ambassador slot
    rocket:    "mascot.rocket",             // legacy/temporary stand-in
    hero:      "hero.rocket",
    supplies:  "logo.supplies",
    affiliate: "affiliate.hero",
    tile:      (slug) => `tile.${slug}`,
  }),

  // ─── Component class presets (locked) ───────────────────────────────────────
  preset: Object.freeze({
    eyebrowClass:  "eyebrow",
    chipGreen:     "chip chip--green",
    chipPurple:    "chip chip--purple",
    btnPrimary:    "btn-lifted",
    btnGhost:      "btn-ghost",
    glassPanel:    "glass-panel",
  }),

  // ─── Z-index layers ─────────────────────────────────────────────────────────
  z: Object.freeze({
    cosmosBg:  0,
    contentBg: 5,
    content:   10,
    header:    40,
    drawer:    45,
    modal:     90,
    ageGate:   100,
  }),
});

export default TOKENS;
